// Retention pressure layer — savings tracking, streaks, missed deals, identity

import { getHistoryEntries, getWatchlistItems, getActivitySummary } from "./watchlistStore";
import { getConfirmedPurchases } from "./intentCapture";

const STREAK_KEY = "decision-os-streak";

interface StreakData {
  currentStreak: number;
  lastActiveDate: string;
  longestStreak: number;
}

function getStreakData(): StreakData {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    if (!raw) return { currentStreak: 0, lastActiveDate: "", longestStreak: 0 };
    return JSON.parse(raw);
  } catch { return { currentStreak: 0, lastActiveDate: "", longestStreak: 0 }; }
}

function saveStreakData(data: StreakData): void {
  try { localStorage.setItem(STREAK_KEY, JSON.stringify(data)); } catch {}
}

export function recordActivity(): StreakData {
  const data = getStreakData();
  const today = new Date().toISOString().slice(0, 10);

  if (data.lastActiveDate === today) return data; // already recorded today

  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (data.lastActiveDate === yesterday) {
    data.currentStreak++;
  } else {
    data.currentStreak = 1;
  }

  data.lastActiveDate = today;
  data.longestStreak = Math.max(data.longestStreak, data.currentStreak);
  saveStreakData(data);
  return data;
}

export function getStreak(): StreakData {
  return getStreakData();
}

// Savings vs average buyer
export function getSavingsInsight(): { totalSaved: number; decisionsCount: number; avgSavingPerDecision: number; vsAverageBuyer: string } {
  const activity = getActivitySummary();
  const history = getHistoryEntries();

  // Simulate: average buyer overpays ~15% on each purchase
  const avgOverpay = 0.15;
  const purchases = getConfirmedPurchases();
  const estimatedSpend = purchases.reduce((sum, p) => {
    const price = parseFloat(p.price.replace(/[^0-9.]/g, "")) || 0;
    return sum + price;
  }, 0);

  const totalSaved = Math.round(estimatedSpend * avgOverpay) + activity.estimatedSavings;
  const decisionsCount = history.length;
  const avgSavingPerDecision = decisionsCount > 0 ? Math.round(totalSaved / decisionsCount) : 0;

  let vsAverageBuyer = "";
  if (totalSaved > 100) vsAverageBuyer = `You've saved $${totalSaved} more than the average buyer`;
  else if (totalSaved > 20) vsAverageBuyer = `You've made $${totalSaved} in smarter decisions`;
  else if (decisionsCount > 0) vsAverageBuyer = `${decisionsCount} informed decisions and counting`;

  return { totalSaved, decisionsCount, avgSavingPerDecision, vsAverageBuyer };
}

// Missed opportunities
export interface MissedDeal {
  productName: string;
  message: string;
  type: "price_increase" | "better_deal_yesterday" | "could_have_saved";
}

export function getMissedDeals(): MissedDeal[] {
  const watchlist = getWatchlistItems();
  const deals: MissedDeal[] = [];

  watchlist.forEach((item) => {
    if (item.priceChange && item.priceChange > 0) {
      deals.push({
        productName: item.productName,
        message: `Price increased ${item.priceChange}% since your last check`,
        type: "price_increase",
      });
    }
  });

  // Simulate occasional missed deals
  if (watchlist.length > 0 && Math.random() > 0.6) {
    const item = watchlist[Math.floor(Math.random() * watchlist.length)];
    const savings = Math.floor(Math.random() * 25) + 5;
    deals.push({
      productName: item.productName,
      message: `A better deal was available yesterday — could have saved $${savings}`,
      type: "better_deal_yesterday",
    });
  }

  return deals.slice(0, 3);
}

// Identity / progression labels
export function getIdentityLabel(decisionsCount: number): string {
  if (decisionsCount >= 30) return "Elite Operator";
  if (decisionsCount >= 20) return "Decision Pro";
  if (decisionsCount >= 12) return "Deal Optimizer";
  if (decisionsCount >= 5) return "Smart Buyer";
  return "Getting Started";
}

// Avoided risky purchases
export function getRiskAvoidance(): { count: number; label: string } {
  const history = getHistoryEntries();
  // Approximate: each decision with high confidence means we filtered risky options
  const highConfidence = history.filter((h) => h.confidence === "high" || h.confidence === "medium").length;
  const riskyAvoided = Math.floor(highConfidence * 1.5); // each decision filters ~1.5 risky options
  return {
    count: riskyAvoided,
    label: riskyAvoided > 0 ? `You avoided ${riskyAvoided} risky purchase${riskyAvoided > 1 ? "s" : ""}` : "",
  };
}
